/**
 * TTS Service
 * 
 * Handles communication with backend TTS API using fetchService pattern
 */

import { Voice, Quality } from '../types/state';
import { API_BASE_URL } from '../../../api/config';
import fetchService from '../../../services/fetchService';

export interface TTSGenerateRequest {
  text: string;
  voice: Voice;
  speed: number;
  quality: Quality;
}

export interface TTSGenerateResponse {
  audio: string;  // Base64-encoded MP3
  duration: number;
  chunks: number;
  character_count: number;
  voice: string;
  speed: number;
  quality: string;
  error?: string;
}

export interface TTSProgressCallback {
  (progress: { percent: number; chunks_done: number; chunks_total: number }): void;
}

class TTSService {
  async generateAudio(
    text: string,
    voice: Voice,
    speed: number,
    quality: Quality,
    onProgress?: TTSProgressCallback
  ): Promise<TTSGenerateResponse> {
    try {
      // Simulate progress for better UX (backend doesn't support streaming yet)
      if (onProgress) {
        const estimatedChunks = Math.ceil(text.length / 4000);
        onProgress({ percent: 0, chunks_done: 0, chunks_total: estimatedChunks });
      }
      
      const result = await fetchService(`${API_BASE_URL}/tts/generate`, {
        method: 'POST',
        body: JSON.stringify({
          text,
          voice,
          speed,
          quality
        })
      });
      
      if (onProgress) {
        onProgress({ percent: 100, chunks_done: result.chunks, chunks_total: result.chunks });
      }
      
      return result;
      
    } catch (error: any) {
      console.error('[TTS_SERVICE] Error generating audio:', error);
      return {
        audio: '',
        duration: 0,
        chunks: 0,
        character_count: 0,
        voice,
        speed,
        quality,
        error: error.message || 'Failed to generate audio'
      };
    }
  }
  
  async getVoices(): Promise<Array<{ id: string; name: string; description: string }>> {
    try {
      const result = await fetchService(`${API_BASE_URL}/tts/voices`, {
        method: 'GET'
      });
      
      return result.voices || [];
      
    } catch (error) {
      console.error('[TTS_SERVICE] Error fetching voices:', error);
      // Return default voices as fallback
      return [
        { id: 'alloy', name: 'Alloy', description: 'Neutral and balanced' },
        { id: 'echo', name: 'Echo', description: 'Warm and conversational' },
        { id: 'fable', name: 'Fable', description: 'Expressive and storytelling' },
        { id: 'onyx', name: 'Onyx', description: 'Deep and authoritative' },
        { id: 'nova', name: 'Nova', description: 'Clear and professional' },
        { id: 'shimmer', name: 'Shimmer', description: 'Bright and energetic' }
      ];
    }
  }
}

// Export singleton instance
export const ttsService = new TTSService();

